import type { FC, ReactNode} from 'react';
import { createContext, useContext, useState, useCallback } from 'react';
import { Alert, Snackbar, useTheme } from '@/shared/ui/mui';
import type { AlertProps } from '@/shared/ui/mui';
export type ToastSeverity = AlertProps['severity'];
export type ToastVariant = AlertProps['variant'];

export interface ToastPosition {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

export interface ToastOptions {
  severity?: ToastSeverity;
  variant?: ToastVariant;
  duration?: number;
  position?: ToastPosition;
  icon?: ReactNode;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
  variant: ToastVariant;
  duration: number;
  position: ToastPosition;
  icon?: ReactNode;
}

interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => void;
  showSuccess: (message: string, options?: Omit<ToastOptions, 'severity'>) => void;
  showError: (message: string, options?: Omit<ToastOptions, 'severity'>) => void;
  showWarning: (message: string, options?: Omit<ToastOptions, 'severity'>) => void;
  showInfo: (message: string, options?: Omit<ToastOptions, 'severity'>) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: FC<ToastProviderProps> = ({ children }) => {
  const theme = useTheme();
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
    variant: 'filled',
    duration: 4000,
    position: { vertical: 'bottom', horizontal: 'center' },
  });

  const showToast = useCallback((message: string, options?: ToastOptions) => {
    setToast({
      open: true,
      message,
      severity: options?.severity || 'info',
      variant: options?.variant || 'filled',
      duration: options?.duration || 4000,
      position: options?.position || { vertical: 'bottom', horizontal: 'center' },
      icon: options?.icon,
    });
  }, []);

  const showSuccess = useCallback((message: string, options?: Omit<ToastOptions, 'severity'>) => {
    showToast(message, { ...options, severity: 'success' });
  }, [showToast]);

  const showError = useCallback((message: string, options?: Omit<ToastOptions, 'severity'>) => {
    showToast(message, { ...options, severity: 'error' });
  }, [showToast]);

  const showWarning = useCallback((message: string, options?: Omit<ToastOptions, 'severity'>) => {
    showToast(message, { ...options, severity: 'warning' });
  }, [showToast]);

  const showInfo = useCallback((message: string, options?: Omit<ToastOptions, 'severity'>) => {
    showToast(message, { ...options, severity: 'info' });
  }, [showToast]);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, open: false }));
  }, []);

  const handleClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    hideToast();
  };

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideToast,
      }}
    >
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.duration}
        onClose={handleClose}
        anchorOrigin={toast.position}
      >
        <Alert
          severity={toast.severity}
          variant={toast.variant}
          onClose={handleClose}
          sx={{
            width: '100%',
            // Ensure proper background colors for filled variant
            ...(toast.variant === 'filled' && {
              '&.MuiAlert-filledSuccess': {
                backgroundColor: theme.palette.success.dark,
                color: theme.palette.common.white,
              },
              '&.MuiAlert-filledError': {
                backgroundColor: theme.palette.error.dark,
                color: theme.palette.common.white,
              },
              '&.MuiAlert-filledWarning': {
                backgroundColor: theme.palette.warning.dark,
                color: theme.palette.common.white,
              },
              '&.MuiAlert-filledInfo': {
                backgroundColor: theme.palette.info.main,
                color: theme.palette.common.white,
              },
            }),
            // Ensure icon and close button are visible
            '& .MuiAlert-icon': {
              color: 'inherit',
            },
            '& .MuiAlert-action': {
              color: 'inherit',
            },
          }}
          {...(toast.icon && { icon: toast.icon })}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
